// @effect-diagnostics nodeBuiltinImport:off - Agent-content inventory digests are computed from on-disk Markdown in trusted packaging hosts.
import * as NodeCrypto from "node:crypto";
import * as NodeFSP from "node:fs/promises";
import * as NodePath from "node:path";

import { ACTIONS } from "./actions.ts";
import {
  PROFILE_IDS,
  SKILL_BUNDLE_IDS,
  type ProfileId,
  type SkillBundleId,
} from "./discovery.ts";

// Slash product paths such as /prototype/<name> remain valid; only reject a
// shipped Skill Bundle name when it ends at the invocation boundary.
const SLASH_STYLE_SKILL_BUNDLE_REFERENCE = new RegExp(
  String.raw`(^|[^$\w-])/(${SKILL_BUNDLE_IDS.join("|")})(?![\w/-])`,
  "m",
);

export {
  PROFILE_IDS,
  SKILL_BUNDLE_IDS,
  USER_FACING_SKILL_BUNDLE_IDS,
  isUserFacingSkillBundleId,
  type ProfileId,
  type SkillBundleId,
  type UserFacingSkillBundleId,
} from "./discovery.ts";

/**
 * Phrases from the retired Thread Scope (ADR 0142). Every thread now runs at its
 * checkout root, so content that still talks about a scope or a Feature Workspace
 * Working Directory would send the agent to the wrong place.
 */
const RETIRED_THREAD_SCOPE_PHRASES = [
  "Thread Scope",
  "Feature Workspace Working Directory",
  "General Chat",
  "cwd-relative",
] as const;

const SKILL_FRONTMATTER = /^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)/u;
const ACTION_DECLARATION = /^afar-action:[ \t]*(.*)$/mu;

/**
 * The Action ids a Skill Bundle's `SKILL.md` declares in its `afar-action`
 * frontmatter field, a comma-separated list. A bundle without the field
 * declares none.
 */
export const skillBundleActionDeclarations = (skillText: string): ReadonlyArray<string> => {
  const frontmatter = SKILL_FRONTMATTER.exec(skillText)?.[1];
  const declaration = frontmatter === undefined ? undefined : ACTION_DECLARATION.exec(frontmatter);
  return (declaration?.[1] ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
};

export const AGENT_CONTENT_PACKAGE_NAME = "@afar-research/agent-content" as const;

const PROJECT_WRITING_PROFILE_GIT_PORTABILITY_REQUIREMENTS = [
  {
    description: "identify the one machine-local exception beneath portable .afar-research content",
    text: "The project-relative `.afar-research/` directory and its descendants are App-owned portable Git content, except `/.afar-research/worktrees/`, which is the App-maintained machine-local Thread Worktree root.",
  },
  {
    description: "forbid ignore rules for other .afar-research content",
    text: "Do not create or modify `.gitignore` rules that ignore other `.afar-research/` content. The exact root-relative `/.afar-research/worktrees/` rule is allowed and must be left in place.",
  },
  {
    description: "report a pre-existing matching ignore rule without changing it",
    text: "If any other matching ignore rule already exists, leave it unchanged and report the portability problem",
  },
] as const;

/**
 * Every profile has to name the Collaborative Browser. Its `preview_*` tools are registered
 * unconditionally, so an agent that is never told the Environment owns a browser cannot tell one it
 * can reach from one it cannot, and reaches for a browser the user cannot see. Computer use was
 * once left out of this on the grounds that its MCP server is absent when the service is off — but
 * when it is on it offers a browser of its own, and an agent handed two in-app browsers reached for
 * the wrong one. It is named here for that reason.
 */
const COLLABORATIVE_BROWSER_PROFILE_REQUIREMENTS = [
  {
    description: "name the browser and the tools that reach it",
    text: "`preview_*` tools on the App Control Tool Surface",
  },
  {
    description: "forbid substituting a browser the user cannot see",
    text: "Playwright, Puppeteer, a Node REPL, a headless browser, `curl`, a provider browser skill",
  },
  {
    description: "say the browser is live only while a client hosts it",
    text: "live only while a client is attached to host it",
  },
  {
    description: "rule out the agent's own computer-use browser",
    text: "computer-use browser",
  },
] as const;

const APP_CONTROL_REQUIRED_GUIDANCE = [
  "AFAR Research App tools",
  "canonical Skill Bundle invocation is `$afar-research`",
  "The MCP tool catalog is the syntax authority.",
  "project_*",
  "worktree_*",
  "thread_*",
  "stable resource identities",
  "bounded watch expiry",
  "Thread Attention",
  "prove that the target is not the current active provider thread",
  "Refuse a wait when the target directly or transitively depends on the current thread",
  "always require a request that authorizes that exact action",
  "afar-research-app-tools-unavailable",
  "afar-research-app-capability-unavailable",
  "afar-research-app-tool-contradiction",
] as const;

const APP_CONTROL_FORBIDDEN_CONTENT = [
  "/Users/",
  "node_modules/",
  ".agents/skills/",
  "Command Line Client",
  "afar-research project",
  "afar-research thread",
  "afar-research worktree",
  "npm install",
  "bun add",
] as const;

export interface AgentContentFile {
  readonly path: string;
  readonly digest: string;
  readonly size: number;
  readonly bytes: Buffer;
}

export interface AgentContentComponent<
  Kind extends "profile" | "skill-bundle" = "profile" | "skill-bundle",
> {
  readonly kind: Kind;
  readonly id: string;
  readonly packageDigest: string;
  readonly inventoryDigest: string;
  readonly files: readonly AgentContentFile[];
}

export interface AgentContentInventory {
  readonly packageName: typeof AGENT_CONTENT_PACKAGE_NAME;
  readonly treeDigest: string;
  readonly profiles: readonly AgentContentComponent<"profile">[];
  readonly skillBundles: readonly AgentContentComponent<"skill-bundle">[];
}

const sha256 = (value: string | Uint8Array): string =>
  NodeCrypto.createHash("sha256").update(value).digest("hex");

const inventoryDigestFor = (files: readonly Omit<AgentContentFile, "bytes">[]): string =>
  sha256(
    [...files]
      .sort((left, right) => left.path.localeCompare(right.path))
      .map((file) => `${file.path}\u0000${file.digest}\u0000${file.size}\n`)
      .join(""),
  );

const packageDigestFor = (files: readonly AgentContentFile[]): string =>
  sha256(
    [...files]
      .sort((left, right) => left.path.localeCompare(right.path))
      .map((file) => `${file.path}\u0000${file.bytes.toString("base64")}\u0000`)
      .join(""),
  );

const listFilesRecursively = async (root: string): Promise<readonly string[]> => {
  const files: string[] = [];
  const visit = async (directory: string): Promise<void> => {
    for (const entry of await NodeFSP.readdir(directory, { withFileTypes: true })) {
      const absolute = NodePath.join(directory, entry.name);
      if (entry.isSymbolicLink()) {
        throw new Error(`Agent-content refuses symlinks: ${absolute}`);
      }
      if (entry.isDirectory()) {
        await visit(absolute);
        continue;
      }
      if (!entry.isFile()) {
        throw new Error(`Agent-content refuses non-file entries: ${absolute}`);
      }
      // macOS Finder metadata must never enter release digests.
      if (entry.name === ".DS_Store") {
        continue;
      }
      files.push(NodePath.relative(root, absolute).split(NodePath.sep).join("/"));
    }
  };
  await visit(root);
  return files.sort();
};

const loadComponentFiles = async (root: string): Promise<readonly AgentContentFile[]> => {
  const relativePaths = await listFilesRecursively(root);
  if (relativePaths.length === 0) {
    throw new Error(`Agent-content component has no files: ${root}`);
  }
  return Promise.all(
    relativePaths.map(async (path) => {
      const bytes = await NodeFSP.readFile(NodePath.join(root, path));
      return {
        path,
        bytes,
        size: bytes.byteLength,
        digest: sha256(bytes),
      };
    }),
  );
};

const loadComponent = async <Kind extends "profile" | "skill-bundle">(input: {
  readonly kind: Kind;
  readonly id: string;
  readonly root: string;
}): Promise<AgentContentComponent<Kind>> => {
  const files = await loadComponentFiles(input.root);
  return {
    kind: input.kind,
    id: input.id,
    files,
    packageDigest: packageDigestFor(files),
    inventoryDigest: inventoryDigestFor(files),
  };
};

/**
 * Read the authored agent content under `packageRoot`.
 *
 * The root is required rather than discovered. Agent content is one of the
 * Program Payload dependencies — a tree of real files the App copies into a
 * Host Store's `components/` — and only the running program knows where its
 * payload landed. This package used to answer that itself with a module-scope
 * directory walk resolved at import time, before the program had learned
 * anything about how it was launched; a compiled executable turns that into a
 * wrong answer rather than an error. Callers resolve the root through the
 * Program Payload Root authority and pass it in.
 */
export const loadAgentContentInventory = async (
  packageRoot: string,
): Promise<AgentContentInventory> => {
  const profiles = await Promise.all(
    PROFILE_IDS.map((id) =>
      loadComponent({
        kind: "profile",
        id,
        root: NodePath.join(packageRoot, "profiles", id),
      }),
    ),
  );
  const skillBundles = await Promise.all(
    SKILL_BUNDLE_IDS.map((id) =>
      loadComponent({
        kind: "skill-bundle",
        id,
        root: NodePath.join(packageRoot, "skills", id),
      }),
    ),
  );
  const treeDigest = sha256(
    [
      ...profiles.map(
        (profile) =>
          `profile\u0000${profile.id}\u0000${profile.packageDigest}\u0000${profile.inventoryDigest}\n`,
      ),
      ...skillBundles.map(
        (bundle) =>
          `skill-bundle\u0000${bundle.id}\u0000${bundle.packageDigest}\u0000${bundle.inventoryDigest}\n`,
      ),
    ].join(""),
  );
  return {
    packageName: AGENT_CONTENT_PACKAGE_NAME,
    treeDigest,
    profiles,
    skillBundles,
  };
};

export const assertAgentContentPolicy = (inventory: AgentContentInventory): void => {
  const profilesById = new Map(inventory.profiles.map((profile) => [profile.id, profile]));
  const profileTexts = new Map<ProfileId, string>();
  for (const profileId of PROFILE_IDS) {
    const profile = profilesById.get(profileId);
    if (profile === undefined) {
      throw new Error("Agent-content inventory is missing required profiles.");
    }
    const text = profile.files
      .find((file) => file.path === "PROFILE.md")
      ?.bytes.toString("utf8");
    if (text === undefined) {
      throw new Error("Agent-content profiles must include PROFILE.md.");
    }
    profileTexts.set(profileId, text);
  }

  const profileText = (profileId: ProfileId): string => profileTexts.get(profileId)!;
  const threadText = profileText("thread");
  for (const forbidden of ["docs/agents", "RTTJogja/afar-research", "Adapted from"]) {
    if ([...profileTexts.values()].some((text) => text.includes(forbidden))) {
      throw new Error(`Agent-content profiles must not contain "${forbidden}".`);
    }
  }
  for (const profileId of PROFILE_IDS) {
    const text = profileText(profileId);
    for (const requirement of PROJECT_WRITING_PROFILE_GIT_PORTABILITY_REQUIREMENTS) {
      if (!text.includes(requirement.text)) {
        throw new Error(
          `${profileId} profile must preserve Git portability: ${requirement.description}.`,
        );
      }
    }
    for (const requirement of COLLABORATIVE_BROWSER_PROFILE_REQUIREMENTS) {
      if (!text.includes(requirement.text)) {
        throw new Error(
          `${profileId} profile must announce the Collaborative Browser: ${requirement.description}.`,
        );
      }
    }
  }
  // The thread profile is sent every turn, so it stays a map: it names where each
  // Feature Workspace lives and which Skill Bundle holds its operating knowledge.
  for (const skill of [
    "$query",
    "$issue-tracker",
    "$triage-labels",
    "$domain-docs",
    "$afar-research",
    "$brain",
    "$design",
    "$experiment",
    "$manuscript",
  ] as const) {
    if (!threadText.includes(skill)) {
      throw new Error(`Thread profile must direct agents to ${skill}.`);
    }
  }
  for (const component of [...inventory.profiles, ...inventory.skillBundles]) {
    for (const file of component.files.filter((candidate) => candidate.path.endsWith(".md"))) {
      const text = file.bytes.toString("utf8");
      const match = SLASH_STYLE_SKILL_BUNDLE_REFERENCE.exec(text);
      if (match !== null) {
        throw new Error(
          `Agent-content ${component.kind} ${component.id}/${file.path} must use $${match[2]} instead of /${match[2]}.`,
        );
      }
      // No thread runs inside a Feature Workspace any more (ADR 0142); content
      // that still assumes one points the agent at the wrong directory.
      for (const retired of RETIRED_THREAD_SCOPE_PHRASES) {
        if (text.includes(retired)) {
          throw new Error(
            `Agent-content ${component.kind} ${component.id}/${file.path} must not mention retired "${retired}".`,
          );
        }
      }
    }
  }
  const skillIds = new Set(inventory.skillBundles.map((bundle) => bundle.id));
  for (const id of SKILL_BUNDLE_IDS) {
    if (!skillIds.has(id)) throw new Error(`Agent-content is missing skill bundle ${id}.`);
  }
  if (inventory.skillBundles.length !== SKILL_BUNDLE_IDS.length) {
    throw new Error("Agent-content skill inventory length mismatch.");
  }
  for (const bundle of inventory.skillBundles) {
    const skillText = bundle.files.find((file) => file.path === "SKILL.md")?.bytes.toString("utf8");
    if (skillText === undefined) {
      throw new Error(`Agent-content skill bundle ${bundle.id} must include SKILL.md.`);
    }
    for (const forbidden of [
      "RTTJogja/afar-research",
      "Adapted from",
      "setup-matt-pocock-skills",
    ] as const) {
      if (skillText.includes(forbidden)) {
        throw new Error(`Agent-content skill ${bundle.id} must not contain "${forbidden}".`);
      }
    }

    const packagedMarkdown = bundle.files.filter((file) => file.path.endsWith(".md"));
    const shipsPackageAssets = bundle.files.some(
      (file) => file.path.startsWith("references/") || file.path.startsWith("scripts/"),
    );
    if (shipsPackageAssets) {
      const packageRootHint =
        /installed package root|directory that contains this `SKILL\.md`|directory containing this `SKILL\.md`/i;
      if (!packageRootHint.test(skillText)) {
        throw new Error(
          `Agent-content skill ${bundle.id} ships references/ or scripts/ and must tell agents to resolve them from this Skill Bundle's installed package root (the directory that contains this SKILL.md).`,
        );
      }
    }
    for (const file of packagedMarkdown) {
      const text = file.bytes.toString("utf8");
      if (/\.agents\/skills\/[A-Za-z0-9_.@+-]+\//.test(text) || /python3\s+\.agents\/skills\//.test(text)) {
        throw new Error(
          `Agent-content skill ${bundle.id} file ${file.path} must not hardcode .agents/skills/ install paths.`,
        );
      }
    }
  }
  const skillText = (id: SkillBundleId): string => {
    const text = inventory.skillBundles
      .find((bundle) => bundle.id === id)
      ?.files.find((file) => file.path === "SKILL.md")
      ?.bytes.toString("utf8");
    if (text === undefined) {
      throw new Error(`Agent-content must include the ${id} Skill Bundle.`);
    }
    return text;
  };
  const brainText = skillText("brain");
  if (!brainText.includes(".afar-research/brain/raw/") || !brainText.includes(".afar-research/brain/wiki/")) {
    throw new Error("brain must describe project-relative .afar-research/brain/raw/ and wiki/.");
  }
  // Each Brain Action invokes its operation Skill Bundle beside this schema, so an
  // operation the schema never names is one the agent answers off its own judgement.
  for (const skill of ["$query", "$ingest", "$lint", "$delete"] as const) {
    if (!brainText.includes(skill)) {
      throw new Error(`brain must direct agents to ${skill}.`);
    }
  }
  if (brainText.replaceAll(".afar-research/brain", "").includes(".brain")) {
    throw new Error("brain must not mention a .brain alias.");
  }
  if (!/never stage,\s*commit,\s*push/i.test(brainText)) {
    throw new Error("brain must forbid automatic commit/push.");
  }
  const designText = skillText("design");
  if (
    !designText.includes(".afar-research/design/design-system/") ||
    !designText.includes("DESIGN.md")
  ) {
    throw new Error(
      "design must describe .afar-research/design/design-system/<slug>/ and DESIGN.md authority.",
    );
  }
  for (const skill of [
    "$create-design",
    "$update-design",
    "$add-design-template",
    "$add-design-example",
    "$create-design-sample",
    "$rename-design-system",
  ] as const) {
    if (!designText.includes(skill)) {
      throw new Error(`design must direct agents to ${skill}.`);
    }
  }
  if (!/never stage,\s*commit,\s*push/i.test(designText)) {
    throw new Error("design must forbid automatic commit/push.");
  }
  for (const bundle of inventory.skillBundles) {
    const declared = skillBundleActionDeclarations(skillText(bundle.id as SkillBundleId));
    const expected = ACTIONS.filter((action) => action.declaredBy === bundle.id).map(
      (action) => action.id,
    );
    if (declared.join(",") !== expected.join(",")) {
      throw new Error(
        `Agent-content skill ${bundle.id} must declare exactly the Actions [${expected.join(", ")}], not [${declared.join(", ")}].`,
      );
    }
  }
  const commandLineClient = inventory.skillBundles.find(
    (bundle) => bundle.id === "afar-research",
  );
  if (commandLineClient === undefined) {
    throw new Error("Agent-content must include the afar-research Skill Bundle.");
  }
  const commandLineClientText = commandLineClient.files
    .find((file) => file.path === "SKILL.md")
    ?.bytes.toString("utf8");
  if (commandLineClientText === undefined) {
    throw new Error("Agent-content must include the afar-research Skill Bundle.");
  }
  for (const guidance of APP_CONTROL_REQUIRED_GUIDANCE) {
    if (!commandLineClientText.includes(guidance)) {
      throw new Error(`afar-research must preserve App control guidance: ${guidance}`);
    }
  }
  if (/\b(?:Codex|Claude)\b/u.test(commandLineClientText)) {
    throw new Error("afar-research must remain provider-neutral without provider-specific copies.");
  }
  for (const forbidden of APP_CONTROL_FORBIDDEN_CONTENT) {
    if (commandLineClientText.includes(forbidden)) {
      throw new Error(`afar-research must not contain installation or host path content: ${forbidden}`);
    }
  }
  const commandReference = commandLineClient.files.find(
    (file) => file.path === "references/tool-surface.json",
  );
  if (commandReference === undefined) {
    throw new Error("afar-research must include its release-owned MCP tool reference.");
  }
  const providerInterface = commandLineClient.files
    .find((file) => file.path === "agents/openai.yaml")
    ?.bytes.toString("utf8");
  if (
    providerInterface === undefined ||
    !providerInterface.includes('display_name: "AFAR Research App tools"')
  ) {
    throw new Error("afar-research must expose its exact product label to provider-native discovery.");
  }
  const createDesignText = skillText("create-design");
  if (!createDesignText.includes("design-system/")) {
    throw new Error("create-design must scaffold under design-system/.");
  }
  if (!createDesignText.includes("Create Design System scaffold")) {
    throw new Error("create-design must document the Create Design System scaffold algorithm.");
  }
  if (createDesignText.includes("afar-research design add")) {
    throw new Error("create-design must not invoke afar-research design add.");
  }
  const addExampleText = skillText("add-design-example");
  if (!addExampleText.includes("Resolve Design System")) {
    throw new Error("add-design-example must document the Resolve Design System algorithm.");
  }
  if (addExampleText.includes("afar-research design resolve")) {
    throw new Error("add-design-example must not invoke afar-research design resolve.");
  }
};
